import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { db } from "@/lib/db-client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { eur, fmtDateTime } from "@/lib/format";
import { FileText } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/invoices")({
  component: IssuedInvoices,
});

function IssuedInvoices() {
  const [rows, setRows] = useState<any[]>([]);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const [invoices, tickets, clients] = await Promise.all([
        db.from("invoices").select("*").order("issued_at", { ascending: false }),
        db.from("weigh_tickets").select("*"),
        db.from("clients").select("*"),
      ]);
      
      const clientsMap = new Map((clients.data || []).map(c => [c.id, c]));
      const ticketsMap = new Map((tickets.data || []).map(t => [t.id, { ...t, clients: clientsMap.get(t.client_id) }]));
      
      const enriched = (invoices.data || []).map(inv => ({
        ...inv,
        weigh_tickets: ticketsMap.get(inv.ticket_id),
      }));
      
      setRows(enriched);
    };
    load();
  }, []);

  const openPdf = async (inv: any) => {
    if (!inv.pdf_path) {
      toast.error("Aucun PDF disponible pour cette facture");
      return;
    }
    setOpening(inv.id);
    const { data, error } = await db.storage
      .from("invoices")
      .createSignedUrl(inv.pdf_path, 60);
    setOpening(null);
    if (error || !data?.signedUrl) {
      toast.error(error?.message ?? "Impossible d'ouvrir le PDF");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Factures émises</h1>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Chantier</TableHead>
                <TableHead>Envoyée à</TableHead>
                <TableHead className="text-right">Montant HT</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Aucune facture</TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono">{r.number}</TableCell>
                  <TableCell>{fmtDateTime(r.issued_at)}</TableCell>
                  <TableCell>{r.weigh_tickets?.clients?.name}</TableCell>
                  <TableCell>{r.weigh_tickets?.site_name}</TableCell>
                  <TableCell className="text-xs">{r.sent_to}</TableCell>
                  <TableCell className="text-right font-medium">{eur(Number(r.total_ht))}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!r.pdf_path || opening === r.id}
                      onClick={() => openPdf(r)}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {opening === r.id ? "…" : "Voir"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
